import React, { Fragment, useEffect, useState } from 'react';
import { Alert, Box, Chip, Collapse, IconButton, LinearProgress, MenuItem, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MDButton from 'components/MDButton';
import CrudService from 'services/cruds-service';
import { adaptTabletLogsResponse, metricText, numberOrNull } from './tabletLogsAdapter';
import MDTypography from 'components/MDTypography';
import { makeStyles } from '@material-ui/core/styles';

function defaultRange() {
  const now = Date.now();
  return { from: new Date(now - 86400000).toISOString().slice(0, 16),
    to: new Date(now + 60000).toISOString().slice(0, 16) };
}

function utcRange(draft) {
  const from = new Date(`${draft.from}:00.000Z`);
  const to = new Date(`${draft.to}:00.000Z`);
  if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) ||
      !(to > from) || to - from > 7 * 86400000) throw new Error('Choose a valid range of up to seven days.');
  return { from: from.toISOString(), to: to.toISOString() };
}

function timeText(value) {
  return value ? new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium', timeStyle: 'medium',
  }).format(new Date(value)) : 'Not reported';
}

function LogDetails({ row }) {
  const transfer = row.transfer;
  const fields = [
    ['Recorded (UTC)', row.recordedAt || 'Not reported'],
    ['Received (UTC)', row.receivedAt || 'Not reported'],
    ['Event', row.event || 'Not reported'], ['Event ID', row.eventId || 'Not reported'],
    ['Motion', row.motion], ['GPS coordinates', row.gps], ['Vehicle link', row.vehicleConnected],
    ['Sensor connection', row.sensorConnection], ['Capacity', metricText(row.capacity, 'gal')],
    ['Remaining until full', metricText(row.remaining, 'gal')], ['Active vehicle faults', metricText(row.faults)],
    ['App version', row.appVersion || 'Not reported'],
  ];
  if (transfer && Object.keys(transfer).length) fields.push(
    ['Transfer type', transfer.type ?? transfer.transfer_type ?? 'Not reported'],
    ['Transfer state', transfer.status ?? transfer.state ?? 'Not reported'],
    ['Reported gallons transferred', metricText(numberOrNull(transfer.gallonsTransferred ?? transfer.gallons_transferred), 'gal')],
    ['Transfer started', transfer.startedAt ?? transfer.started_at ?? 'Not reported'],
    ['Transfer stopped', transfer.stoppedAt ?? transfer.stopped_at ?? 'Not reported'],
  );
  return (
    <Box sx={{ py: 2 }}>
      {row.issues.map((issue) => <Alert severity="warning" key={issue} sx={{ mb: 1 }}>{issue}</Alert>)}
      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '190px minmax(0, 1fr)' }, gap: 1, m: 0 }}>
        {fields.map(([label, value]) => <Fragment key={label}>
          <Typography component="dt" variant="body2" color="text">{label}</Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{String(value)}</Typography>
        </Fragment>)}
      </Box>
      {row.readings.length > 0 && <Box mt={2}>
        <Typography variant="subtitle2">Vehicle readings</Typography>
        {row.readings.map((reading) => <Typography key={reading.key} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {reading.label}: {metricText(reading.value, reading.unit)}{reading.stale ? ' (Stale)' : ''}
        </Typography>)}
      </Box>}
    </Box>
  );
}

const useStyles = makeStyles({
  customTableHead: {
    display: 'table-header-group!important', // Use the correct CSS property without quotes
  },
});

export default function TabletTelemetryLogs({ deviceId }) {
  const [draftRange, setDraftRange] = useState(defaultRange);
  const [range, setRange] = useState(() => utcRange(draftRange));
  const [limit, setLimit] = useState(25);
  const [cursors, setCursors] = useState([null]);
  const [revision, setRevision] = useState(0);
  const [rangeError, setRangeError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [result, setResult] = useState(null);
  const classes = useStyles();

  const beforeId = cursors[cursors.length - 1];
  const queryKey = JSON.stringify([deviceId, range.from, range.to, limit, beforeId, revision]);
  const current = result?.key === queryKey ? result : { loading: true, rows: [], error: '', pagination: {} };

  useEffect(() => {
    let active = true;
    setResult({ key: queryKey, loading: true, rows: [], error: '', pagination: {} });
    setExpanded({});
    CrudService.getTabletTelemetryLogs(deviceId, { ...range, limit, beforeId })
      .then((response) => {
        if (Number(response.device_id) !== Number(deviceId)) throw new Error('Mismatched device');
        const data = adaptTabletLogsResponse(response);
        if (active) setResult({ key: queryKey, loading: false, error: '', ...data });
      })
      .catch((failure) => {
        const code = failure?.error || failure?.response?.data?.error;
        if (active) setResult({ key: queryKey, loading: false, rows: [], pagination: {},
          error: code === 'FORBIDDEN' ? 'You do not have permission to view these logs.' : 'Tablet logs could not be loaded. Try again.' });
      });
    return () => { active = false; };
  }, [queryKey, deviceId, range.from, range.to, limit, beforeId]);

  const applyRange = () => {
    try { setRange(utcRange(draftRange)); setCursors([null]); setRevision((value) => value + 1); setRangeError(''); }
    catch (failure) { setRangeError(failure.message); }
  };
  const refresh = () => { setCursors([null]); setRevision((value) => value + 1); };
  const displayZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField label="Received From (UTC)" type="datetime-local" value={draftRange.from} size="small"
          InputLabelProps={{ shrink: true }} disabled={current.loading}
          onChange={(event) => setDraftRange((previous) => ({ ...previous, from: event.target.value }))} />
        <TextField label="Received To (UTC)" type="datetime-local" value={draftRange.to} size="small"
          InputLabelProps={{ shrink: true }} disabled={current.loading}
          onChange={(event) => setDraftRange((previous) => ({ ...previous, to: event.target.value }))} />
        <MDButton onClick={applyRange} disabled={current.loading} color="info">Apply range</MDButton>
        <TextField select label="Rows" value={limit} size="small" sx={{ minWidth: 90, '& .MuiOutlinedInput-root': { height: '2rem' } }} disabled={current.loading}
          onChange={(event) => { setLimit(Number(event.target.value)); setCursors([null]); }}>
          {[25, 50, 100].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
        </TextField>
        <Tooltip title="Refresh newest page"><span><IconButton aria-label="Refresh logs" onClick={refresh} disabled={current.loading}><RefreshIcon /></IconButton></span></Tooltip>
      </Box>
      <MDTypography variant="caption" color="text">Times displayed in {displayZone}. Range applies to received time.</MDTypography>
      {rangeError && <Alert severity="error" sx={{ mt: 1 }}>{rangeError}</Alert>}
      {current.loading && <LinearProgress sx={{ mt: 1 }} />}
      {current.error && <Alert severity="error" sx={{ mt: 1 }} action={<MDButton onClick={refresh}>Retry</MDButton>}>{current.error}</Alert>}
      {!current.loading && !current.error && current.rows.length === 0 && <Alert severity="info" sx={{ mt: 1 }}>No tablet telemetry received in this time range.</Alert>}
      <TableContainer sx={{ overflowX: 'auto', mt: 1 }}>
        <Table size="small" aria-label="Tablet telemetry history" sx={{ minWidth: 850 }}>
          <TableHead classes={{ root: classes.customTableHead }}>
            <TableRow>
              {['Recorded time', 'Type', 'Tank', 'Volume', 'Pressure', 'Speed', 'Condition', 'Details'].map((label) => <TableCell key={label}>{label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {current.rows.map((row) => <Fragment key={row.id}>
            <TableRow hover>
              <TableCell>{timeText(row.recordedAt)}</TableCell><TableCell>{row.type}</TableCell>
              <TableCell>{metricText(row.level, '%')}</TableCell><TableCell>{metricText(row.volume, 'gal')}</TableCell>
              <TableCell>{metricText(row.pressure, 'psi')}</TableCell><TableCell>{metricText(row.speed, 'mph')}</TableCell>
              <TableCell><Chip size="small" label={row.condition} color={row.issues.length ? 'warning' : 'default'} /></TableCell>
              <TableCell><Tooltip title={expanded[row.id] ? 'Hide details' : 'Show details'} />
                <IconButton size="small" aria-label={`Details for log ${row.id}`} aria-expanded={Boolean(expanded[row.id])}
                  onClick={() => setExpanded((previous) => ({ ...previous, [row.id]: !previous[row.id] }))}>
                  {expanded[row.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow>
              {/* <TableCell colSpan={8} sx={{ py: 0, borderBottom={expanded[row.id] ? undefined : 0 }}}> */}
              <TableCell colSpan={8} sx={{ py:0 }} style={{ borderBottom: expanded[row.id] ? undefined : 0 }} >
              <Collapse in={Boolean(expanded[row.id])} unmountOnExit><LogDetails row={row} /></Collapse>
              </TableCell>
            </TableRow>
          </Fragment>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1 }}>
        <MDTypography variant="caption">Page {cursors.length}</MDTypography>
        <Tooltip title="Previous page"><span><IconButton aria-label="Previous page" disabled={current.loading || cursors.length === 1}
          onClick={() => setCursors((previous) => previous.slice(0, -1))}><ChevronLeftIcon /></IconButton></span></Tooltip>
        <Tooltip title="Next page"><span><IconButton aria-label="Next page" disabled={current.loading || !current.pagination.has_more || !current.pagination.next_before_id}
          onClick={() => setCursors((previous) => [...previous, current.pagination.next_before_id])}><ChevronRightIcon /></IconButton></span></Tooltip>
      </Box>
    </Box>
  );
}
